import { Search, Tabs } from '@bfe/moly';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ImageWithDefault } from '@web3/components';
import { formatNumber } from '@web3/utils';
import clsx from 'clsx';
import { useMemo, useRef, useState } from 'react';
import { useChainList } from '../../store/chainList';
import { useLocale } from '../../store/i18nMessage';
import { useCurrentWallet } from '../../store/userWallet';
import { TokenItem, TokenItemWithChain } from '../../types';
import { invoke } from '../../wallet-hub/misc';

export type TokenPanelProps = {
  className?: string;
  initialChainCode?: string;
  onSelectToken?: (token: TokenItemWithChain) => void;
};

export function TokenPanel({ className, initialChainCode = 'ALL', onSelectToken }: TokenPanelProps) {
  const [search, setSearch] = useState('');
  const { t } = useLocale();
  const [chainCode, setChainCode] = useState(initialChainCode);
  const { chainList: cloudChainList, evmChains, getChain } = useChainList();

  const { chainBalanceList, isBrowserWallet } = useCurrentWallet();

  const chainList = isBrowserWallet ? cloudChainList : evmChains;

  const allTokenList = useMemo(() => {
    const unSortedTokenList = invoke(() => {
      if (chainCode === 'ALL') {
        return chainBalanceList.reduce((acc, chain) => acc.concat(chain.tokenAsset ?? []), [] as TokenItem[]);
      } else {
        return chainBalanceList.find((w) => w.chainCode === chainCode)?.tokenAsset ?? [];
      }
    });
    const tokenList = unSortedTokenList.sort((a, b) => +b.balanceUsd - +a.balanceUsd);
    return tokenList.map((t) => {
      const token: TokenItemWithChain = {
        ...t,
        chain: getChain(t.chainCode),
      };
      return token;
    });
  }, [chainCode, chainBalanceList, getChain]);

  const tokenList = useMemo(() => {
    const txt = String.prototype.toUpperCase.call(search.startsWith('0x') ? search.slice(2) : search);
    if (!txt) {
      return allTokenList;
    }
    return allTokenList.filter(({ symbol, contract }) => {
      return symbol.toUpperCase().includes(txt) || contract?.toLowerCase() === txt.toLowerCase();
    });
  }, [search, allTokenList]);

  const containerRef = useRef<HTMLDivElement>(null);
  const virtual = useVirtualizer({
    count: tokenList.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 56,
  });

  return (
    <div className={clsx('flex flex-col', className)}>
      <div className="flex-none px-8">
        <Search
          value={search}
          onChange={(e) => setSearch(e.target.value.trim())}
          placeholder={t('swap_select_token_placeholder')}
          className="rounded-[100px]"
        />
        <Tabs
          size="small"
          showScrollButton
          className="my-2"
          activeKey={chainCode}
          onChange={(e) => setChainCode(e)}
          items={[
            {
              label: 'ALL',
              key: 'ALL',
            },
          ].concat(
            chainList.map((c) => ({
              key: c.chainCode,
              label: c.chainName,
            })),
          )}
        ></Tabs>
      </div>
      <div
        ref={containerRef}
        className="flex flex-col flex-1 overflow-auto web3-scrollbar pr-1 "
        style={{
          minHeight: 380,
          maxHeight: 600,
        }}
      >
        <ul
          className="mt-4 w-full relative"
          style={{
            height: `${virtual.getTotalSize()}px`,
          }}
        >
          {virtual.getVirtualItems().map((vItem) => {
            const item = tokenList[vItem.index];
            return (
              <li
                key={vItem.key}
                className="flex items-center py-2 px-8 transition-all min-h-14 hover:bg-bds-trans-hover cursor-pointer"
                onClick={() => {
                  onSelectToken?.(item);
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${vItem.size}px`,
                  transform: `translateY(${vItem.start}px)`,
                }}
              >
                <div className="relative ">
                  <ImageWithDefault size={28} className="rounded-full" src={item.iconDayUrl} />
                  <ImageWithDefault
                    size={12}
                    className="absolute bottom-0 right-[-2px] border border-solid border-white bg-white rounded-full"
                    src={item.chain?.iconDayUrl}
                  />
                </div>
                <h3 className="ltr:ml-[6px] rtl:mr-[6px] text-sm font-medium">{item.symbol}</h3>
                <div className=" ltr:ml-auto rtl:mr-auto text-right">
                  <p className=" text-sm font-medium">{item.balance ? item.balance.toLocaleString() : '--'}</p>
                  <p className="text-xs mt-[2px] text-bds-gray-t2">
                    {item.balanceUsd
                      ? `$${formatNumber(item.balanceUsd, {
                          roundMode: 7,
                        })}`
                      : '--'}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
